import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";
import { 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  RefreshCw,
  ArrowRight,
  ShieldCheck,
  Home
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Account type labels for display
const ACCOUNT_LABELS = {
  parent: "Parent",
  teacher: "Teacher",
  student: "Student",
  school_admin: "School Administrator"
};

export default function ReactivatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState("loading"); // loading, success, error, invalid
  const [message, setMessage] = useState("");
  const [accountType, setAccountType] = useState("");
  
  // Extract params from URL
  const type = searchParams.get("type");
  const id = searchParams.get("id");
  const token = searchParams.get("token");
  
  useEffect(() => {
    const verifyMagicLink = async () => {
      // Validate required params
      if (!type || !id || !token) {
        setStatus("invalid");
        setMessage("Invalid reactivation link. Please request a new one from the login page.");
        return;
      }
      
      // Validate account type
      if (!["parent", "teacher", "student", "school_admin"].includes(type)) {
        setStatus("invalid");
        setMessage("Invalid account type in the link.");
        return;
      }
      
      setAccountType(ACCOUNT_LABELS[type] || type);
      
      try {
        const response = await fetch(
          `${API}/lifecycle/reactivation/magic-link?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`
        );
        
        const data = await response.json();
        
        if (response.ok && data.success) {
          setStatus("success");
          setMessage("Your account has been successfully reactivated!");
        } else {
          setStatus("error");
          setMessage(data.detail || data.error || "Failed to reactivate account. The link may have expired.");
        }
      } catch (err) {
        setStatus("error");
        setMessage("Unable to connect to the server. Please try again later.");
      }
    };
    
    verifyMagicLink();
  }, [type, id, token]);
  
  const handleGoToLogin = () => {
    // Redirect based on account type
    if (type === "school_admin") {
      navigate("/school/login");
    } else if (type === "teacher") {
      navigate("/teacher/login");
    } else {
      navigate("/login");
    }
  };
  
  const handleRequestNewLink = () => {
    // Go to login page where they can request reactivation again
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/20 flex flex-col">
      {/* Header - Using SharedHeader for consistency */}
      <SharedHeader 
        rightContent={
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-slate-600 hover:text-emerald-600">
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </Link>
        }
      />
      
      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md shadow-xl border-0 overflow-hidden">
          {/* Status Header */}
          <div className={`p-6 text-center ${
            status === "success" 
              ? "bg-gradient-to-r from-emerald-500 to-teal-500" 
              : status === "loading"
              ? "bg-gradient-to-r from-blue-500 to-indigo-500"
              : "bg-gradient-to-r from-red-500 to-rose-500"
          }`}>
            {status === "loading" && (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
                <h1 className="text-xl font-bold text-white">Verifying Your Link</h1>
                <p className="text-white/80 text-sm">Please wait while we reactivate your account...</p>
              </div>
            )}
            
            {status === "success" && (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center animate-bounce-once">
                  <CheckCircle2 className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-xl font-bold text-white">Account Reactivated!</h1>
                <p className="text-white/80 text-sm">Welcome back to Eduzy</p>
              </div>
            )}
            
            {(status === "error" || status === "invalid") && (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-xl font-bold text-white">
                  {status === "invalid" ? "Invalid Link" : "Reactivation Failed"}
                </h1>
                <p className="text-white/80 text-sm">We could not process your request</p>
              </div>
            )}
          </div>
          
          {/* Content Body */}
          <CardContent className="p-6">
            {status === "loading" && (
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
                <p className="text-sm text-slate-500">This should only take a moment</p>
              </div>
            )}
            
            {status === "success" && (
              <div className="space-y-6">
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-emerald-800 font-medium">{message}</p>
                      {accountType && (
                        <p className="text-emerald-600 text-sm mt-1">
                          Your {accountType} account is now active.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 text-center">
                    Your account has been fully restored. All your learning progress and data are safe.
                  </p>
                  
                  <Button 
                    onClick={handleGoToLogin}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white py-6"
                    data-testid="continue-to-login-btn"
                  >
                    Continue to Login
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
            
            {(status === "error" || status === "invalid") && (
              <div className="space-y-6">
                <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                  <p className="text-red-800 text-sm">{message}</p>
                </div>
                
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 text-center">
                    {status === "invalid" 
                      ? "The link you clicked appears to be invalid or incomplete."
                      : "The reactivation link may have expired (links are valid for 24 hours)."}
                  </p>
                  
                  <Button 
                    onClick={handleRequestNewLink}
                    className="w-full bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white py-6"
                    data-testid="request-new-link-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Request New Link
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full"
                  >
                    Go to Homepage
                  </Button>
                </div>
                
                <div className="text-center pt-4 border-t">
                  <p className="text-xs text-slate-500">
                    Need help? Contact us at{" "}
                    <a href="mailto:info@contentiumtech.com" className="text-emerald-600 hover:underline">
                      info@contentiumtech.com
                    </a>
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      
      {/* Footer */}
      <SharedFooter />
      
      {/* Custom Animation Styles */}
      <style>{`
        @keyframes bounce-once {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        .animate-bounce-once {
          animation: bounce-once 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}
